"use client";

import React, { useState, useRef, useEffect } from "react";
import dynamic from "next/dynamic";
import { motion, AnimatePresence } from "framer-motion";
import {
  Mail,
  Instagram,
  Youtube,
  Clock4,
  Facebook,
  Linkedin,
} from "lucide-react";
import { PiTiktokLogo } from "react-icons/pi";
import { RxDiscordLogo } from "react-icons/rx";
import { FaXTwitter } from "react-icons/fa6";
import { LiaTelegramPlane } from "react-icons/lia";
import { useTranslations } from "next-intl";
import type { ContactFaqItem } from "./ContactFaqSection";
import type { ContactOptionGuideItem } from "./ContactOptionsGuideSection";

function SectionLayoutFallback({ minHeight }: { minHeight: number }) {
  return (
    <div className="w-full bg-[#191919]" style={{ minHeight }} aria-hidden />
  );
}

// Below-the-fold CMS sections - dynamic import to reduce initial JS bundle.
const ContactHelpTopicsSection = dynamic(
  () => import("./ContactHelpTopicsSection"),
  { loading: () => <SectionLayoutFallback minHeight={480} /> },
);
const ContactOptionsGuideSection = dynamic(
  () => import("./ContactOptionsGuideSection"),
  { loading: () => <SectionLayoutFallback minHeight={360} /> },
);
const ContactMessageDetailsSection = dynamic(
  () => import("./ContactMessageDetailsSection"),
  { loading: () => <SectionLayoutFallback minHeight={300} /> },
);
const ContactFeedbackSection = dynamic(
  () => import("./ContactFeedbackSection"),
  { loading: () => <SectionLayoutFallback minHeight={220} /> },
);
const ContactBeforeContactSection = dynamic(
  () => import("./ContactBeforeContactSection"),
  { loading: () => <SectionLayoutFallback minHeight={280} /> },
);
const ContactHereToHelpSection = dynamic(
  () => import("./ContactHereToHelpSection"),
  { loading: () => <SectionLayoutFallback minHeight={240} /> },
);
const ContactFaqSection = dynamic(() => import("./ContactFaqSection"), {
  loading: () => <SectionLayoutFallback minHeight={560} />,
});
const ContactStillNeedHelpSection = dynamic(
  () => import("./ContactStillNeedHelpSection"),
  { loading: () => <SectionLayoutFallback minHeight={200} /> },
);

interface FormState {
  name: string;
  email: string;
  subject: string;
  message: string;
}

const initialFormState: FormState = {
  name: "",
  email: "",
  subject: "",
  message: "",
};

export type ContactPageCmsContent = {
  heroTitle?: string;
  heroSubtitle?: string;
  helpTopicsTitle?: string;
  helpTopicsDescription?: string;
  helpTopics?: ContactFaqItem[];
  contactOptionsTitle?: string;
  contactOptionsDescription?: string;
  contactOptions?: ContactOptionGuideItem[];
  messageDetailsTitle?: string;
  messageDetailsBody?: string;
  messageDetailsListTitle?: string;
  messageDetailsItems?: string[];
  feedbackTitle?: string;
  feedbackBody?: string;
  beforeContactTitle?: string;
  beforeContactBody?: string;
  hereToHelpTitle?: string;
  hereToHelpBody?: string;
  faqTitle?: string;
  faqItems?: ContactFaqItem[];
  stillNeedHelpTitle?: string;
  stillNeedHelpDescription?: string;
  stillNeedHelpButtonLabel?: string;
  stillNeedHelpButtonLink?: string;
};

interface ContactPageContentProps {
  cms?: ContactPageCmsContent;
}

export const ContactPageContent: React.FC<ContactPageContentProps> = ({
  cms,
}) => {
  const t = useTranslations("Contact");
  const formSectionRef = useRef<HTMLElement>(null);

  const [formData, setFormData] = useState<FormState>(initialFormState);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [showSuccess, setShowSuccess] = useState(false);
  const [highlightFields, setHighlightFields] = useState({
    name: false,
    email: false,
    subject: false,
    message: false,
  });
  const successTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const highlightTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    return () => {
      if (successTimeoutRef.current) {
        clearTimeout(successTimeoutRef.current);
      }
      if (highlightTimeoutRef.current) {
        clearTimeout(highlightTimeoutRef.current);
      }
    };
  }, []);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>,
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (highlightFields[name as keyof typeof highlightFields]) {
      setHighlightFields((prev) => ({ ...prev, [name]: false }));
    }
  };

  const handleScheduleMeeting = () => {
    const bookingPageUrl =
      process.env.NEXT_PUBLIC_GOOGLE_CALENDAR_BOOKING_URL ||
      "https://calendar.app.google/KvUrCWyT6eZ9iY9L8";
    window.open(bookingPageUrl, "_blank", "noopener,noreferrer");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setShowSuccess(false);

    if (successTimeoutRef.current) {
      clearTimeout(successTimeoutRef.current);
      successTimeoutRef.current = null;
    }

    const trimmedName = formData.name.trim();
    const trimmedEmail = formData.email.trim();
    const trimmedSubject = formData.subject.trim();
    const trimmedMessage = formData.message.trim();

    const invalidFields = {
      name: !trimmedName,
      email: !trimmedEmail,
      subject: !trimmedSubject,
      message: !trimmedMessage,
    };

    if (
      invalidFields.name ||
      invalidFields.email ||
      invalidFields.subject ||
      invalidFields.message
    ) {
      setError(t("form.requiredError"));

      // Highlight invalid required fields for 0.75 s, then fade back.
      setHighlightFields(invalidFields);
      if (highlightTimeoutRef.current) clearTimeout(highlightTimeoutRef.current);
      highlightTimeoutRef.current = setTimeout(() => {
        setHighlightFields({ name: false, email: false, subject: false, message: false });
        highlightTimeoutRef.current = null;
      }, 750);

      setLoading(false);
      return;
    }

    const nameParts = trimmedName.split(/\s+/);
    const firstName = nameParts[0] || "";
    const lastName = nameParts.slice(1).join(" ") || "";

    try {
      const response = await fetch("/api/contact-us", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          firstName,
          lastName,
          email: trimmedEmail,
          subject: trimmedSubject,
          message: trimmedMessage,
        }),
      });

      if (!response.ok) {
        const result = await response.json().catch(() => ({}));
        throw new Error(result.error || "Failed to send message.");
      }

      setShowSuccess(true);
      setFormData(initialFormState);
      setHighlightFields({ name: false, email: false, subject: false, message: false });

      successTimeoutRef.current = setTimeout(() => {
        setShowSuccess(false);
        successTimeoutRef.current = null;
      }, 3000);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : "Something went wrong.");
    } finally {
      setLoading(false);
    }
  };

  /** Returns the border-color class for a required field based on highlight state. */
  const fieldBorderClass = (field: keyof typeof highlightFields) =>
    highlightFields[field] ? "border-[#767EF6]" : "border-[#615D5D]";

  const heroTitle = cms?.heroTitle?.trim() || t("hero.title");
  const heroSubtitle = cms?.heroSubtitle?.trim() || t("hero.subtitle");
  const helpTopicsTitle = cms?.helpTopicsTitle;
  const helpTopicsDescription = cms?.helpTopicsDescription;
  const helpTopics = cms?.helpTopics;
  const contactOptionsTitle = cms?.contactOptionsTitle;
  const contactOptionsDescription = cms?.contactOptionsDescription;
  const contactOptions = cms?.contactOptions;
  const messageDetailsItems = cms?.messageDetailsItems;
  const faqTitle = cms?.faqTitle;
  const faqItems = cms?.faqItems;
  const stillNeedHelpTitle =
    cms?.stillNeedHelpTitle?.trim() || "Still need help?";
  const stillNeedHelpDescription =
    cms?.stillNeedHelpDescription?.trim() ||
    "Send us a message and our team will be happy to assist you.";
  const stillNeedHelpButtonLabel =
    cms?.stillNeedHelpButtonLabel?.trim() || t("form.button");
  const stillNeedHelpButtonLink = cms?.stillNeedHelpButtonLink?.trim();

  return (
    <div className="min-h-screen bg-[#191919] text-[#F5F5F5]">
      <main className=" mx-auto">
        {/* Hero */}
        <section className="relative mb-10 overflow-hidden rounded-none w-full">
          <div
            className="relative flex items-center justify-center px-4 py-[200px] w-full min-h-[400px]"
            style={{
              backgroundImage:
                "linear-gradient(to bottom, rgba(0,0,0,0.65), rgba(0,0,0,0.75)), url('/images/contact-us/contact-hero.jpg')",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundRepeat: "no-repeat",
              backgroundAttachment: "scroll",
            }}
          >
            <div className="max-w-3xl text-center text-[#F5F5F5] z-10">
              <h1 className="text-3xl sm:text-4xl md:text-5xl font-extrabold mb-4">
                {heroTitle}
              </h1>
              <p className="text-[22px]  max-w-xl mx-auto  font-semibold leading-relaxed">
                {heroSubtitle}
              </p>
            </div>
          </div>
        </section>

        {/* Get in touch cards */}
        <section className="mb-10 max-w-4xl md:mx-auto mx-5">
          <div className="text-center mb-8">
            <h2 className="text-4xl mt-20 font-bold text-[#767EF6] mb-2">
              {t("getInTouch.title")}
            </h2>
            <p className="text-sm sm:text-base text-[#F5F5F5]/80">
              {t("getInTouch.subtitle")}
            </p>
          </div>

          <div className="flex flex-col gap-4 md:flex-row  ">
            <div className="md:w-[260px] flex-shrink-0 ">
              <a
                href="mailto:info@pinegen.ai"
                className="flex h-full min-h-[190px] flex-col items-center justify-center rounded-2xl border border-[#615D5D] bg-[#232325] px-10 py-8 text-center hover:bg-[#2a2a2f] transition-colors"
              >
                <span className="mb-6 inline-flex h-12 w-12 items-center justify-center rounded-full   text-[#F5F5F5]">
                  <Mail className="h-[56px] w-[56px]" />
                </span>
                <p className="text-xl font-semibold text-[#F5F5F5] mb-2">
                  {t("cards.email.label")}
                </p>
                <p className="text-md text-[#F5F5F5]/70">info@pinegen.ai</p>
              </a>
            </div>

            <div className="flex-1 ">
              <div className="flex flex-col md:flex-row h-full min-h-[190px] md:items-center md:justify-between gap-6 rounded-2xl border border-[#615D5D] bg-[#232325] px-8 py-8">
                <div className="flex flex-col p-4 items-center justify-center">
                  <span className="mb-3 inline-flex h-12 w-12 items-center justify-center rounded-full   text-[#F5F5F5]">
                    <Clock4 className="h-[56px] w-[56px]" />
                  </span>
                  <p className="text-xl font-semibold text-[#F5F5F5] mb-1 text-center">
                    {t("cards.meeting.label")}
                  </p>
                  <p className="text-[16px] font-semibold text-[#F5F5F5]/70 text-center">
                    {t("cards.meeting.timeRange")}
                  </p>
                  <p className="text-[16px] text-[#F5F5F5]/70 text-center">
                    {t("cards.meeting.days")}
                  </p>
                </div>
                <div className="flex-shrink-0 flex justify-center md:justify-end">
                  <button
                    type="button"
                    onClick={handleScheduleMeeting}
                    className="inline-flex items-center justify-center rounded-[25px] bg-[#3541ED] px-6 py-2 text-xs font-semibold text-[#F5F5F5] hover:bg-[#2932b8] transition-colors cursor-pointer"
                  >
                    <span className="text-[16px] font-semibold text-[#F5F5F5] py-[12px] px-[10px]">
                      {t("cards.meeting.button")}
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Form card */}
        <section
          ref={formSectionRef}
          id="contact-form"
          className="mb-6 max-w-4xl md:mx-auto mx-5 scroll-mt-24"
        >
          <div className="rounded-2xl border border-[#615D5D] bg-[#232325] px-6 py-10 sm:px-12 sm:py-12">
            <div className="text-center mb-8">
              <p className="text-[24px] font-medium text-[#F5F5F5] mb-1">
                {t("form.sectionLabel")}
              </p>
              <h2 className="text-2xl sm:text-3xl font-semibold text-[#767EF6]">
                {t("form.title")}
              </h2>
            </div>

            <form
              onSubmit={handleSubmit}
              className="space-y-4 max-w-xl mx-auto"
            >
              <div>
                <input
                  id="name"
                  name="name"
                  type="text"
                  value={formData.name}
                  onChange={handleChange}
                  placeholder="Name"
                  aria-invalid={Boolean(highlightFields.name)}
                  aria-describedby={error ? "contact-form-error" : undefined}
                  className={`h-11 w-full rounded-[6px] border-2 ${fieldBorderClass("name")} bg-[#191919] px-3 text-sm text-center text-[#F5F5F5] placeholder:text-[#8E8A8A] outline-none focus:border-[#767EF6] transition-colors duration-300`}
                />
              </div>

              <div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Email"
                  aria-invalid={Boolean(highlightFields.email)}
                  aria-describedby={error ? "contact-form-error" : undefined}
                  className={`h-11 w-full rounded-[6px] border-2 ${fieldBorderClass("email")} bg-[#191919] px-3 text-sm text-center text-[#F5F5F5] placeholder:text-[#8E8A8A] outline-none focus:border-[#767EF6] transition-colors duration-300`}
                />
              </div>

              <div>
                <input
                  id="subject"
                  name="subject"
                  type="text"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Subject"
                  aria-invalid={Boolean(highlightFields.subject)}
                  aria-describedby={error ? "contact-form-error" : undefined}
                  className={`h-11 w-full rounded-[6px] border-2 ${fieldBorderClass("subject")} bg-[#191919] px-3 text-sm text-center text-[#F5F5F5] placeholder:text-[#8E8A8A] outline-none focus:border-[#767EF6] transition-colors duration-300`}
                />
              </div>

              <div>
                <textarea
                  id="message"
                  name="message"
                  rows={4}
                  value={formData.message}
                  onChange={handleChange}
                  placeholder="Message"
                  aria-invalid={Boolean(highlightFields.message)}
                  aria-describedby={error ? "contact-form-error" : undefined}
                  className={`w-full rounded-[6px] border-2 ${fieldBorderClass("message")} bg-[#212121] px-3 py-8 text-sm text-center text-[#F5F5F5] placeholder:text-[#8E8A8A] outline-none focus:border-[#767EF6] resize-none transition-colors duration-300`}
                  style={{ maxHeight: "100px" }}
                />
              </div>

              <div className="min-h-[20px] text-xs text-center">
                {error ? (
                  <p id="contact-form-error" role="alert" className="text-red-400">
                    {error}
                  </p>
                ) : null}
              </div>

              <div className="flex justify-center pt-2">
                <button
                  type="submit"
                  disabled={loading}
                  className="inline-flex min-w-[190px] items-center justify-center rounded-full bg-[#3541ED] px-[55px] py-[10px] text-[16px] font-semibold text-[#F5F5F5] hover:bg-[#2932b8] disabled:opacity-60 disabled:cursor-not-allowed transition-colors "
                >
                  {loading ? (
                    <span>{t("form.sending")}</span>
                  ) : (
                    <span>{t("form.button")}</span>
                  )}
                </button>
              </div>
            </form>
          </div>
        </section>

        {/* Follow Us */}
        <section className=" max-w-4xl md:mx-auto mx-10 px-4 mt-40 mb-20">
          <div className="text-center">
            <h2 className="text-3xl sm:text-4xl font-extrabold mb-3">
              <span className="text-[#767EF6]">{t("follow.titlePart1")}</span>{" "}
              <span className="text-[#5A63D4]">{t("follow.titlePart2")}</span>
            </h2>
            <p className="text-[26px] font-semibold  text-[#F5F5F5] mb-8">
              {t("follow.subtitle")}
            </p>

            <div className="flex flex-wrap items-center justify-center gap-4">
              <a
                href="https://www.facebook.com/PineGenAi/"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Facebook"
                className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-[#615D5D] bg-[#232325] text-[#F5F5F5] hover:text-[#767EF6] hover:bg-[#191919] transition-colors"
              >
                <Facebook className="h-5 w-5" />
              </a>
              <a
                href="https://www.tiktok.com/@pinegenai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="TikTok"
                className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-[#615D5D] bg-[#232325] text-[#F5F5F5] hover:text-[#767EF6] hover:bg-[#191919] transition-colors"
              >
                <PiTiktokLogo className="h-5 w-5" />
              </a>
              <a
                href="https://www.linkedin.com/showcase/pinegen-ai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="LinkedIn"
                className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-[#615D5D] bg-[#232325] text-[#F5F5F5] hover:text-[#767EF6] hover:bg-[#191919] transition-colors"
              >
                <Linkedin className="h-5 w-5" />
              </a>
              <a
                href="https://t.me/PineGenAI"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Telegram"
                className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-[#615D5D] bg-[#232325] text-[#F5F5F5] hover:text-[#767EF6] hover:bg-[#191919] transition-colors"
              >
                <LiaTelegramPlane className="h-5 w-5" />
              </a>
              <a
                href="https://discord.gg/dH9ETzQrbJ"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Discord"
                className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-[#615D5D] bg-[#232325] text-[#F5F5F5] hover:text-[#767EF6] hover:bg-[#191919] transition-colors"
              >
                <RxDiscordLogo className="h-5 w-5" />
              </a>
              <a
                href="https://x.com/PineGenAI"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="X (Twitter)"
                className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-[#615D5D] bg-[#232325] text-[#F5F5F5] hover:text-[#767EF6] hover:bg-[#191919] transition-colors"
              >
                <FaXTwitter className="h-5 w-5" />
              </a>
              <a
                href="https://www.instagram.com/pinegenai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Instagram"
                className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-[#615D5D] bg-[#232325] text-[#F5F5F5] hover:text-[#767EF6] hover:bg-[#191919] transition-colors"
              >
                <Instagram className="h-5 w-5" />
              </a>
              <a
                href="https://www.youtube.com/@pinegenai"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="YouTube"
                className="inline-flex h-12 w-12 items-center justify-center rounded-lg border border-[#615D5D] bg-[#232325] text-[#F5F5F5] hover:text-[#767EF6] hover:bg-[#191919] transition-colors"
              >
                <Youtube className="h-5 w-5" />
              </a>
            </div>
          </div>
        </section>

        {/* What We Can Help You With (CMS) */}
        {helpTopics && helpTopics.length > 0 ? (
          <ContactHelpTopicsSection
            title={helpTopicsTitle}
            description={helpTopicsDescription}
            items={helpTopics}
          />
        ) : null}

        {/* Which contact option should you choose? (CMS) */}
        {contactOptions && contactOptions.length > 0 ? (
          <ContactOptionsGuideSection
            title={contactOptionsTitle}
            description={contactOptionsDescription}
            items={contactOptions}
          />
        ) : null}

        {/* What to include in your message (CMS) */}
        <ContactMessageDetailsSection
          title={cms?.messageDetailsTitle}
          body={cms?.messageDetailsBody}
          listTitle={cms?.messageDetailsListTitle}
          items={messageDetailsItems}
        />

        {/* Send feedback or share an idea (CMS) */}
        <ContactFeedbackSection
          title={cms?.feedbackTitle}
          body={cms?.feedbackBody}
        />

        {/* Before you contact us (CMS) */}
        <ContactBeforeContactSection
          title={cms?.beforeContactTitle}
          body={cms?.beforeContactBody}
        />

        {/* We're here to help (CMS) */}
        <ContactHereToHelpSection
          title={cms?.hereToHelpTitle}
          body={cms?.hereToHelpBody}
        />

        {/* FAQ (CMS) */}
        {faqItems && faqItems.length > 0 ? (
          <ContactFaqSection title={faqTitle} items={faqItems} />
        ) : null}

        {/* Still need help? (CMS) */}
        <ContactStillNeedHelpSection
          title={stillNeedHelpTitle}
          description={stillNeedHelpDescription}
          buttonLabel={stillNeedHelpButtonLabel}
          buttonLink={stillNeedHelpButtonLink}
        />
      </main>

      <AnimatePresence>
        {showSuccess ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.2 }}
            className="fixed inset-0 z-50 flex items-center justify-center px-4"
            aria-modal="true"
            aria-live="polite"
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-[#191919]/70 backdrop-blur-md"
            />
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              transition={{ duration: 0.25 }}
              className="relative z-10 max-w-md rounded-2xl border border-emerald-500/40 bg-[#232325] px-8 py-6 text-center"
            >
              <p className="text-base font-medium text-emerald-200">
                {t("form.success")}
              </p>
            </motion.div>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default ContactPageContent;
